import { isNotFound, retryTransient } from '@agos/api-client'
import { useGetMarketingSlug } from '@agos/api-client/platform'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { applyFavicon } from './favicon'
import { NotPublished, SectionView, SiteUnreachable } from './Sections'
import {
  cssVariables,
  siteAttributes,
  documentMeta,
  drawable,
  missingNote,
  unreachableNote,
  publishedPageOf,
  shareImage,
  skipped,
  socialMeta,
  structuredData,
} from './site'

/**
 * The platform's own shopfront, rendering what the marketing editor published.
 *
 * `GET /api/v1/marketing/{slug}` is public and serves only `sections` — the draft never leaves the
 * editor. A 404 here means *not published yet*, which to a visitor is the same as *not there*, and is
 * drawn as such rather than as a failure.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A marketing page changes when somebody publishes, not while it is being read.
      refetchOnWindowFocus: false,
      staleTime: 5 * 60_000,
      /* FB-833, §3.158 - **retry a fault, never an answer**, which is the sentence `pageIsMissing` already carries
         (FB-829, §9.544) one level up. This was `retry: false`, justified by the 404 above - right about the 404 and
         wrong about everything else: a 500, a 429 or a dropped connection got **one** attempt, so §3.154's *could not
         load* fired on a single transient failure, on the public site, with the visitor's only recourse a button.
         **FB-834, §9.548 moved the rule into `errors.ts`** - this had a local `isAnswer` listing 404 and 401, and the
         shared `worthRetrying` is stricter: every 4xx except 408 and 429 is final, so a 403 is no longer retried three
         times for nothing.
         **Two attempts, not three**, because this is the shopfront: the cost is a visitor watching a blank page, and a
         third lands after roughly four seconds of nothing. §3.154's sentence and its retry remain for what two do not
         fix. */
      retry: retryTransient,
    },
  },
})

function Site() {
  const { data, error, isLoading, isError, refetch } = useGetMarketingSlug('home')
  const page = useMemo(() => publishedPageOf(data), [data])
  const meta = documentMeta(page)

  useEffect(() => {
    // §4: SEO matters here. The title and description are written into the document once the page has
    // loaded; `index.html` carries fallbacks so a crawler that arrives during a failure sees something.
    document.title = meta.title
    const tag = document.querySelector('meta[name="description"]')
    if (tag !== null) tag.setAttribute('content', meta.description)
  }, [meta.title, meta.description])

  useEffect(() => {
    // §4.3, FB-480: the share card has to describe what was published, not what was built. The static
    // head is the fallback; this is the half a crawler reads when the page rendered.
    if (page === null) return
    const url = window.location.origin + window.location.pathname
    const image = shareImage(page, window.location.origin)
    for (const [selector, content] of socialMeta(page, url, image)) {
      // The tag is created if the head does not have it. `index.html` ships all of them, so in
      // production this only ever rewrites - but a head somebody trims should not silently drop a tag.
      let element = document.head.querySelector<HTMLMetaElement>(selector)
      if (element === null) {
        const attribute = /\[(\w+)='([^']+)'\]/.exec(selector)
        if (attribute === null) continue
        element = document.createElement('meta')
        element.setAttribute(attribute[1]!, attribute[2]!)
        document.head.appendChild(element)
      }
      element.setAttribute('content', content)
    }

    let jsonLd = document.head.querySelector<HTMLScriptElement>("script[type='application/ld+json']")
    if (jsonLd === null) {
      jsonLd = document.createElement('script')
      jsonLd.type = 'application/ld+json'
      document.head.appendChild(jsonLd)
    }
    jsonLd.textContent = JSON.stringify(structuredData(page, url))
  }, [page])

  useEffect(() => {
    // §4.2, FB-479: the owner's brand colour and font, which were parsed and then thrown away. Set on
    // the root element so every section reads them through the design tokens - legacy's own reason,
    // *"so every section can react without prop drilling"*.
    if (page === null) return
    const root = document.documentElement
    const applied = cssVariables(page.settings)
    for (const [name, value] of Object.entries(applied)) root.style.setProperty(name, value)
    /* §3.166 - the theme and the corner radius, which legacy has set from these same settings all along and
     * AGOS set neither of. On the root for the reason the colours are: `index.css` keys its rules on
     * `:root[data-theme=…]`, so every section follows with no prop drilling. */
    const attributes = siteAttributes(page.settings)
    for (const [name, value] of Object.entries(attributes)) root.setAttribute(name, value)
    return () => {
      // Removed on unmount rather than left behind, so a settings change that drops a colour does not
      // keep rendering the old one. Nothing in production unmounts this, and a test does.
      for (const name of Object.keys(applied)) root.style.removeProperty(name)
      for (const name of Object.keys(attributes)) root.removeAttribute(name)
    }
  }, [page])

  useEffect(() => {
    /* §3.181, FB-954 - the published page's own tab icon, which legacy has set from these same settings all
     * along and AGOS set from nothing. §3.180 shipped the static default an hour before this, which is the
     * order that matters: **the owner's page leaves `logo` as `""`**, so an override with no default behind it
     * would have been a control that changes nothing (§3.132).
     *
     * **That reason read *"carries empty `settings`"* until 2026-10-01 and was false** - the server's key is
     * `siteSettings`, which their page fills, and it is the **logo alone** that is blank. `site.ts`'s own
     * docstring records the same mis-measurement; the conclusion here held for the wrong reason.
     *
     * **Created if the head has none**, exactly as legacy's does and as `socialMeta`'s loop above already does
     * for a trimmed head - and `index.html` ships the tag, so in production this only ever rewrites.
     *
     * **The previous href is restored on unmount rather than the tag being removed.** The static one is the
     * app's own answer, not a leftover, so dropping it would leave a visitor with the browser's generic glyph -
     * which is the whole of FB-953. Nothing in production unmounts this; a test does. */
    if (page === null) return
    return applyFavicon(document.head, page.settings.faviconUrl)
  }, [page])

  if (isLoading) return <main data-site-loading className="min-h-screen" />
  /* FB-825, §3.154 - **two states, two sentences.** `isError || page === null` collapsed a read that did not answer
     into *this page is not published*, which is a positive fact about the owner's business told to every visitor who
     arrived during a backend blip. The unpublished sentence keeps its exact wording, because it is right about the
     state it names. */
  /* §3.162 - **a 404 is the server answering, and this branch used to call it a failure.** §3.154 split the two
     states correctly and then tested react-query's raw `isError`, which is true for a 404 - so a page that really
     was unpublished told the visitor *we could not load this, try again*, which is the defect §3.154 fixed with
     the sides swapped. This file's own line 24 already said what a 404 means: *"A 404 here means not published
     yet, which to a visitor is the same as not there."* `isNotFound` is §9.544's rule, moved to where `ApiError`
     lives so the customer site and this one cannot disagree about it. */
  if (isError && !isNotFound(error)) {
    return <SiteUnreachable note={unreachableNote()} retry={() => void refetch()} />
  }
  if (page === null) return <NotPublished note={missingNote()} />

  const missing = skipped(page)

  return (
    <main data-site={page.slug}>
      {drawable(page).map((section) => (
        <SectionView key={section.id} section={section} />
      ))}
      {/* A type the editor published that this build cannot draw. Never shown to a visitor as an
          error - it is a deployment fact, and the marker exists so a screenshot pair notices. */}
      {missing.length > 0 && <span hidden data-skipped-sections={missing.join(',')} />}
    </main>
  )
}

export function App() {
  const [client] = useState(() => queryClient)
  return (
    <QueryClientProvider client={client}>
      <Site />
    </QueryClientProvider>
  )
}
